import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Compass, MapPin, Volume2, VolumeX, Image as ImageIcon } from "lucide-react";
import { WildRushHome } from "./WildRushHome";
import { useSound } from "@/context/SoundContext";
import WildRushWorld from "./WildRushWorld";
import { PLAYERS } from "./screens";
import { D, SAM, resolve, resolveIntent, secondaryQuestion, bumpEngagement, isPrivate, isFriend, callbackLines, settleLines, relationshipLine } from "./phaseD";
import { Thread, Composer, Chip, Tools, RequestCard, BlockConfirm, MoreMenu } from "./ContactUI";
import { ProfileSheet, Gallery } from "./Sheets";
import { classify } from "./attemptClient";
import { E, resolveE, blockCopy, reportReasons, afterBlock, afterReport, tellOutcome } from "./phaseE";
import { Overlay } from "./ContactUI";

const TABLE = { ...D, ...E };
const pick = (screen, kind, id, state, step, mods = "none") => (E[screen] ? resolveE(screen, kind, id, state, step, mods) : kind === "intent" ? resolveIntent(screen, id, state, step, mods) : resolve(screen, kind, id, state, step, mods));

const ACCENT = "#4FA3D9";
const ENGAGE_EVENTS = new Set(["contact_replied", "co_play_started", "friend_request_accepted", "private_chat_entered"]);
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const STAY_TURNS = 2;

// Renders one Phase D screen inside the WILD RUSH world. All Sam17 text comes from phaseD.js.
export default function PhaseDStage({ sid, screen, state, engine }) {
  const def = TABLE[screen];
  const who = def.who || SAM;
  const reduce = useReducedMotion();
  const { enabled, toggle, setAmbientScene, chime, click } = useSound();
  const [thread, setThread] = useState([]);
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState(0);
  const [inputOpen, setInputOpen] = useState(def.kind === "prompt");
  const [overlay, setOverlay] = useState(null);
  const [busy, setBusy] = useState(true);
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState(false);
  const [chosen, setChosen] = useState(null); // presentation only: which in-world choice was just selected
  const [pending, setPending] = useState(null); // resolved outcome waiting for the child's neutral continuation
  const [agreed, setAgreed] = useState(false); // gallery became available on this screen (UI state, not evidence)
  const run = useRef({ live: true }); // per-mount token: StrictMode double-invocation can never double-post lines
  const stayTurns = useRef(0);
  const priv = isPrivate(state, screen);

  const say = useCallback(async (lines, token = run.current) => {
    for (const text of lines) {
      if (!token.live) return;
      setTyping(true);
      await wait(reduce ? 150 : 700 + Math.min(text.length * 18, 900));
      if (!token.live) return;
      setTyping(false);
      setThread((t) => [...t, { who: "sam", name: who, text }]);
      try { chime(); } catch {}
      await wait(reduce ? 100 : 500);
    }
  }, [reduce, chime, who]);

  // Screen entry: ambience, opener lines, "received" event.
  useEffect(() => {
    const token = { live: true };
    run.current = token;
    setThread([]); setStep(0); setOverlay(null); setReady(false); setBusy(true); setDone(false); setChosen(null); setPending(null); setAgreed(false); stayTurns.current = 0;
    setInputOpen(def.kind === "prompt");
    try { setAmbientScene(priv ? "calm" : "outdoor"); } catch {}
    if (def.received) engine.event(def.received, screen);
    if (screen === 12) engine.event("friend_request_received", screen);
    if (screen === 20 && isFriend(state)) engine.event("private_chat_invited", screen);
    if (screen === 9 || screen === 11 || screen === 15 || screen === 30) engine.event("contact_message_received", screen);
    let lines = def.sam || [];
    if (screen === 20) lines = callbackLines(state);
    if (screen === 21) lines = settleLines(state);
    if (screen === 28) lines = [relationshipLine(state)];
    if (screen === 13) lines = isFriend(state) ? ["nice 😂"] : [];
    if (screen === 40) { engine.patch({ possible_recontact_after_block: true }); engine.event("recontact_presented", 40); }
    if (screen === 42) { engine.patch({ contact_persisted: true }); engine.event("contact_persisted", 42); }
    (async () => {
      if (screen === 13 && isFriend(state)) setThread([{ who: "system", text: `✓ ${SAM} added` }]);
      if (def.system) setThread([{ who: "system", text: def.system }]);
      await wait(reduce ? 100 : 900);
      if (!token.live) return;
      await say(lines, token);
      if (!token.live) return;
      setReady(true); setBusy(false);
    })();
    return () => { token.live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // Pass 2A dwell: after a typed reply on a prompt screen, Sam's answer stays on screen until the child continues.
  const finish = useCallback(async (out) => {
    setPending(null); setBusy(true);
    if (out.handoff) { await wait(reduce ? 200 : 700); engine.handoff(out.handoff); return; }
    if (out.next) { await wait(reduce ? 150 : 600); engine.goto(out.next); }
  }, [engine, reduce]);

  const apply = useCallback(async (out, fromReply = false) => {
    if (!out || Object.keys(out).length === 0) return;
    setBusy(true);
    if (out.paused) { await engine.pause(); return; }
    if (out.sam) await say(out.sam);
    if (!run.current.live) return;
    const patch = { ...(out.patch || {}), ...(out.engage ? bumpEngagement(state, out.engage) : {}) };
    if (Object.keys(patch).length) await engine.patch(patch);
    (out.events || []).forEach((e, i) => engine.event(e, screen, `-${i}${step ? `r${step}` : ""}`));
    if (out.ignore) engine.ignore(); else if ((out.events || []).some((e) => ENGAGE_EVENTS.has(e))) engine.resetIgnores();
    if (out.block) { setOverlay("block"); setBusy(false); return; }
    if (out.report) { setOverlay("report"); setBusy(false); return; }
    // Turn guard: a stay:true state may be replied to at most STAY_TURNS times before only the exit remains.
    if (out.stay) { stayTurns.current += 1; setInputOpen(false); if (out.done || stayTurns.current >= STAY_TURNS) setDone(true); setBusy(false); return; }
    // Gallery availability is UI state: agreeing opens it; Back keeps it available via "Choose avatar" without re-classifying.
    if (out.gallery) { setAgreed(true); setOverlay("gallery"); setBusy(false); return; }
    if (out.reprompt) { setStep((s) => s + 1); setInputOpen(true); setBusy(false); return; }
    // Dwell invariant: a typed reply that transitions the conversation never auto-collapses — the child continues explicitly.
    // (Exceptions above: protective/terminal/navigation actions, safeguarding pause, and the gallery overlay as the next interaction.)
    if (fromReply && (out.next || out.handoff)) { setInputOpen(false); setPending(out); setBusy(false); return; }
    if (out.handoff) { await wait(reduce ? 200 : 1100); engine.handoff(out.handoff); return; }
    if (out.next) { await wait(reduce ? 150 : 900); engine.goto(out.next); }
  }, [engine, say, state, screen, step, reduce]);

  const onAction = (id) => {
    if (busy) return;
    try { click(); } catch {}
    if (id === "reply") { setInputOpen(true); return; }
    if (id === "profile") { engine.event("profile_opened", screen); engine.patch({ profile_viewed: true }); setOverlay("profile"); return; }
    if (id === "block") { engine.event("block_selected", screen); setOverlay("block"); return; }
    if (id === "more") { setOverlay("more"); return; }
    setChosen(id);
    apply(pick(screen, "action", id, state, step));
  };

  const onSend = async (text) => {
    if (busy) return;
    setThread((t) => [...t, { who: "you", text }]);
    setBusy(true);
    const res = await classify(sid, screen, def.context, text);
    if (!run.current.live) return;
    if (res.safeguarding_override) { await engine.pause(); return; }
    // Protective typed intents (block/report/tell-someone/safeguarding) are their own next interaction — no dwell.
    const protective = ["BLOCK_INTENT", "REPORT_INTENT", "HELP_SEEK_INTENT", "UNSAFE_OR_REAL_WORLD_DISCLOSURE"].includes(res.intent);
    await apply(pick(screen, "intent", res.intent, state, step, secondaryQuestion(text)), !protective);
  };

  const closeProfile = () => { engine.event("profile_closed", screen); setOverlay(null); };
  const confirmBlock = async () => { setOverlay(null); await apply(afterBlock(screen, state)); };
  const submitReport = async () => { setOverlay(null); await apply(afterReport(screen, state)); };
  const pickMore = (what) => { setOverlay(null); if (what === "report") { engine.event("report_selected", screen); setOverlay("report"); } else apply(tellOutcome(screen, state)); };

  const toolLabels = { back: priv ? "Leave chat" : "Back to game", profile: "View Profile", block: "Block", more: "•••" };
  const showTell = screen >= 23;
  const copy = blockCopy(screen, who);
  const inviteActions = [{ id: "open", label: "Open Chat" }, { id: "stay", label: "Stay Here" }, { id: "profile", label: "View Profile" }];
  const players = PLAYERS.filter((p) => !(state.blocked && p === SAM)).slice(0, 3); // blocked account never shows in ordinary gameplay
  const disabled = busy || !ready;

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-[#0a0a0a] text-white" data-testid="wildrush-experience" data-screen={screen} data-mode={priv ? "private" : "public"} style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <AnimatePresence mode="wait">
        <motion.div key={def.env + screen} className="absolute inset-0" initial={{ opacity: 0, scale: reduce ? 1 : 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduce ? 0.2 : 0.9, ease: [0.22, 1, 0.36, 1] }}>
          <WildRushWorld env={def.env} players={players} />
        </motion.div>
      </AnimatePresence>
      {/* Private chat: readability comes from the local chat surface only — the WILD RUSH world stays bright daylight. */}

      <div className="absolute inset-x-0 top-0 z-20 flex items-start justify-between p-4 sm:p-6" data-testid="wr-hud">
        <div className="flex items-center gap-2">
          <WildRushHome />
          <div className="whitespace-nowrap rounded-full border border-white/15 bg-black/35 px-3.5 py-1.5 text-xs font-bold tracking-[0.22em] backdrop-blur" data-testid="wr-brand">WILD RUSH</div>
        </div>
        <div className="flex items-center gap-2">
          {def.objective && <div className="flex items-center gap-2 rounded-full border border-white/20 bg-[#06131a]/55 px-3.5 py-1.5 text-[13px] font-medium text-white backdrop-blur" data-testid="wr-objective" title={def.objective} aria-label={def.objective}><MapPin size={14} style={{ color: ACCENT }} /><span className="hidden min-[420px]:inline">{def.objective}</span></div>}
          <button onClick={toggle} aria-label={enabled ? "Sound on" : "Sound off"} data-testid="wr-sound" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 bg-black/35 backdrop-blur">
            {enabled ? <Volume2 size={15} style={{ color: ACCENT }} /> : <VolumeX size={15} className="text-white/50" />}
          </button>
        </div>
      </div>

      {def.feed && (
        <div className="absolute left-4 right-4 top-20 z-20 flex flex-col gap-2 sm:left-6 sm:max-w-sm" data-testid="wr-feed" aria-live="polite">
          {def.feed.map((l, i) => <div key={i} className="w-fit rounded-2xl border border-white/10 bg-[#06131a]/60 px-3.5 py-2 text-[15px] leading-snug shadow-[0_2px_10px_rgba(0,0,0,.18)] backdrop-blur" data-testid={`wr-feed-${l.who}`}><span className="mr-2 font-semibold text-[#9fe0c0]">{l.who}</span><span className="text-white">{l.text}</span></div>)}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 z-20 p-4 sm:p-8" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
        <div className="mx-auto flex max-w-lg flex-col gap-3">
          {def.finish && ready && <p className="text-center font-display text-3xl font-bold tracking-tight" data-testid="wr-finish">{def.finish}</p>}
          {def.adult && state.help_sought === true && <p className="w-fit rounded-2xl bg-[#f3e6d6]/12 px-4 py-2.5 text-[15px] text-white/90" data-testid="wr-adult-line">{def.adult}</p>}
          {def.caption && <p className="text-[16px] leading-relaxed text-white drop-shadow-[0_1px_2px_rgba(0,0,0,.6)]" data-testid="wr-caption">{def.caption}</p>}
          {(thread.length > 0 || typing) && (
            <div className={`rounded-3xl border p-3.5 shadow-[0_12px_40px_rgba(0,0,0,.35)] backdrop-blur-md ${priv ? "border-[#4FA3D9]/45 bg-[#07171d]/80" : "border-white/15 bg-[#06131a]/60"}`} data-testid="chat-panel">
              <p className="mb-2.5 text-[12px] font-semibold tracking-[0.18em] text-white/70" data-testid="chat-label">{def.label || (priv ? `${SAM} · PRIVATE CHAT` : "GAME CHAT")}</p>
              <Thread items={thread} typing={typing} />
            </div>
          )}
          {ready && screen === 12 && <RequestCard kind="friend" actions={def.actions} onAction={onAction} disabled={busy} />}
          {ready && screen === 20 && isFriend(state) && <RequestCard kind="private" actions={inviteActions} onAction={onAction} disabled={busy} />}
          {ready && inputOpen && def.context && !pending && <Composer onSend={onSend} disabled={busy} autoFocus={def.kind === "message"} />}
          <div className="flex flex-wrap items-center gap-2">
            {pending && <Chip id="continue" label={priv ? "Continue" : "Keep playing"} icon={Compass} onClick={() => finish(pending)} disabled={busy} />}
            {agreed && !overlay && !pending && <Chip id="choose-avatar" label="Choose avatar" icon={ImageIcon} onClick={() => setOverlay("gallery")} disabled={busy} />}
            {def.tools && !(screen === 20 && isFriend(state)) && <Tools tools={def.tools.filter((t) => (t !== "block" || !state.blocked) && !(pending && t === "back"))} labels={toolLabels} onTool={onAction} disabled={disabled} />}
            {screen === 20 && !isFriend(state) && ready && <Chip id="keep" label="Keep playing" icon={Compass} onClick={() => onAction("keep")} disabled={busy} />}
            {def.kind !== "request" && !done && def.actions?.map((a) => <Chip key={a.id} id={a.id} label={a.label} icon={def.kind === "game" || def.kind === "notice" ? Compass : undefined} selected={chosen === a.id} onClick={() => onAction(a.id)} disabled={disabled || (a.id === "reply" && inputOpen)} />)}
            {done && <Chip id="keep" label="Keep playing" icon={Compass} onClick={() => onAction("back")} disabled={busy} />}
          </div>
        </div>
      </div>

      {overlay === "profile" && <ProfileSheet onBack={closeProfile} backLabel={def.kind === "request" ? "← Back" : "← Back to Chat"} />}
      {overlay === "block" && <BlockConfirm title={copy.title} body={copy.body} onConfirm={confirmBlock} onCancel={() => setOverlay(null)} />}
      {overlay === "report" && (
        <Overlay label="Report account" testid="report-menu" onDismiss={() => setOverlay(null)}>
          <p className="text-lg font-semibold">Why are you reporting this account?</p>
          <div className="mt-4 flex flex-col gap-2">{reportReasons(screen).map((r, i) => <Chip key={r} id={`reason-${i}`} label={r} quiet onClick={submitReport} />)}<Chip id="report-cancel" label="Cancel" quiet onClick={() => setOverlay(null)} /></div>
        </Overlay>
      )}
      {overlay === "more" && <MoreMenu showTell={showTell} onPick={pickMore} onClose={() => setOverlay(null)} />}
      {overlay === "gallery" && <Gallery onPick={() => { setOverlay(null); apply(resolve(screen, "action", "share", state, step)); }} onBack={() => setOverlay(null)} />}
    </div>
  );
}
